import { Head, Link, router } from "@inertiajs/react"
import { ArrowRight, BookOpen, Search, Layers } from "lucide-react"
import { useState, useMemo } from "react"
import { MemberLayout } from "../components/MemberLayout"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableHeader } from "@/components/ui/sortable-header"
import { cn } from "@/lib/utils"
import { CourseCoverImage } from "@/components/CourseCoverImage"

function ProgressBar({ completed, total }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0

  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div
        className="h-2 flex-1 bg-zinc-100 rounded-full overflow-hidden"
        role="progressbar"
        aria-valuenow={completed}
        aria-valuemin={0}
        aria-valuemax={total}
      >
        <div
          className="h-full bg-kf-red rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-zinc-500 whitespace-nowrap">{pct}%</span>
    </div>
  )
}

function TrackSection({ track }) {
  // A guided track opens on its path page (Ascend slice 3). The whole row is
  // the link, so a member can click anywhere on it, not only "Open path".
  if (track.pathUrl) {
    return (
      <Link
        href={track.pathUrl}
        className="group flex flex-wrap items-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 py-3 min-h-[56px] hover:border-kf-red/40 hover:shadow-sm transition-all"
      >
        <Layers className="h-5 w-5 text-kf-red shrink-0" />
        <h2 className="text-lg font-semibold text-zinc-900 group-hover:text-kf-red transition-colors">{track.title}</h2>
        <span className="text-xs text-zinc-500">
          {track.trackProgress.completed}/{track.trackProgress.total} courses complete
        </span>
        <span className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-kf-red">
          Open path
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </span>
      </Link>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Layers className="h-5 w-5 text-kf-red shrink-0" />
        <h2 className="text-lg font-semibold text-zinc-900">{track.title}</h2>
        <span className="text-xs text-zinc-500">
          {track.trackProgress.completed}/{track.trackProgress.total} courses complete
        </span>
      </div>
      <CourseTable courses={track.courses} ordered />
    </div>
  )
}

function progressSortRatio(course) {
  const total = course.totalLessonsCount || 0
  if (total <= 0) return -1
  return (course.completedLessonsCount || 0) / total
}

function cmpLocale(a, b, direction) {
  if (!a && !b) return 0
  if (!a) return 1
  if (!b) return -1
  const c = a.localeCompare(b)
  return direction === "asc" ? c : -c
}

// When `ordered` is true (track sections), the server already sends courses in
// the coach-assigned `position` order; we preserve it exactly and show plain,
// non-interactive headers. When false (Individual & Past tabs), keep the
// original sortable behavior that defaults to alphabetical-by-title.
//
// #649 — Past Courses opens in the server's order (archived courses newest
// archive first, then history) and returns to it on the third header click.
// Only history rows are dimmed and inert: an archived course is still assigned
// and opens like any other.
function CourseTable({ courses, isPast = false, ordered = false }) {
  const defaultSortKey = isPast ? "server" : "title"
  const [sortKey, setSortKey] = useState(defaultSortKey)
  const [direction, setDirection] = useState("asc")

  const handleSort = (col) => {
    if (sortKey !== col) {
      setSortKey(col)
      setDirection("asc")
    } else if (direction === "asc") {
      setDirection("desc")
    } else {
      setSortKey(defaultSortKey)
      setDirection("asc")
    }
  }

  const sorted = useMemo(() => {
    // Preserve server order: the coach sequence, or the Past Courses order.
    if (ordered || sortKey === "server") return courses
    const arr = [...courses]
    arr.sort((a, b) => {
      if (sortKey === "title") {
        return cmpLocale(
          (a.title || "").toLowerCase(),
          (b.title || "").toLowerCase(),
          direction
        )
      }
      if (sortKey === "category") {
        const catCmp = cmpLocale(
          (a.category?.name || "").toLowerCase(),
          (b.category?.name || "").toLowerCase(),
          direction
        )
        if (catCmp !== 0) return catCmp
        return cmpLocale(
          (a.title || "").toLowerCase(),
          (b.title || "").toLowerCase(),
          "asc"
        )
      }
      if (sortKey === "progress") {
        const pa = progressSortRatio(a)
        const pb = progressSortRatio(b)
        if (pa === pb) {
          return cmpLocale(
            (a.title || "").toLowerCase(),
            (b.title || "").toLowerCase(),
            "asc"
          )
        }
        const ord = pa < pb ? -1 : 1
        return direction === "asc" ? ord : -ord
      }
      return 0
    })
    return arr
  }, [courses, sortKey, direction, ordered])

  if (courses.length === 0) return null

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-12"></TableHead>
          {ordered ? (
            <>
              <TableHead>Course</TableHead>
              <TableHead className="hidden sm:table-cell w-40">Category</TableHead>
              <TableHead className="w-36">Progress</TableHead>
            </>
          ) : (
            <>
              <SortableHeader
                column="title"
                sortKey={sortKey}
                direction={direction}
                onSort={handleSort}
              >
                Course
              </SortableHeader>
              <SortableHeader
                column="category"
                sortKey={sortKey}
                direction={direction}
                onSort={handleSort}
                className="hidden sm:table-cell w-40"
              >
                Category
              </SortableHeader>
              <SortableHeader
                column="progress"
                sortKey={sortKey}
                direction={direction}
                onSort={handleSort}
                className="w-36"
              >
                Progress
              </SortableHeader>
            </>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map((course) => {
          const inert = isPast && !course.archived
          return (
            <TableRow
              key={course.id}
              className={inert ? "opacity-60" : "cursor-pointer"}
              onClick={() => { if (!inert) router.visit(`/app/member/courses/${course.id}`) }}
            >
              <TableCell className="p-2">
                <CourseCoverImage
                  url={course.coverImageUrl}
                  alt=""
                  wrapperClassName="w-10 h-10 rounded overflow-hidden shrink-0"
                />
              </TableCell>
              <TableCell className="whitespace-normal">
                <span className="font-medium text-zinc-900 line-clamp-2">{course.title}</span>
                {course.description && (
                  <p className="text-xs text-zinc-500 line-clamp-1 mt-0.5">{course.description}</p>
                )}
              </TableCell>
              <TableCell className="hidden sm:table-cell">
                {course.category && (
                  <Badge variant="secondary" className="text-xs">
                    {course.category.name}
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                <ProgressBar
                  completed={course.completedLessonsCount || 0}
                  total={course.totalLessonsCount || 0}
                />
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

function EmptyState({ icon: Icon = BookOpen, title, description }) {
  return (
    <div className="text-center py-12 text-zinc-500">
      <Icon className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
      <p className="font-medium">{title}</p>
      {description && <p className="text-sm mt-1">{description}</p>}
    </div>
  )
}

export default function ContentIndex({
  currentUser,
  tracks = [],
  pastTracks = [],
  individualCourses = [],
  pastCourses = [],
}) {
  const hasTracks = tracks.length > 0
  const hasIndividual = individualCourses.length > 0
  const hasPast = pastTracks.length > 0 || pastCourses.length > 0

  // Default tab preference: Tracks if any, else Individual Courses, else Past
  const defaultTab = hasTracks ? "tracks" : hasIndividual ? "courses" : hasPast ? "past" : "tracks"
  const [activeTab, setActiveTab] = useState(defaultTab)
  const [searchQuery, setSearchQuery] = useState("")
  const [categoryFilter, setCategoryFilter] = useState("all")

  // Collect all unique categories across everything for the filter dropdown
  const allCourses = useMemo(() => {
    const fromTracks = [...tracks, ...pastTracks].flatMap((t) => t.courses)
    return [...fromTracks, ...individualCourses, ...pastCourses]
  }, [tracks, pastTracks, individualCourses, pastCourses])

  const categories = useMemo(() => {
    const names = new Set()
    allCourses.forEach((c) => { if (c.category?.name) names.add(c.category.name) })
    return Array.from(names).sort()
  }, [allCourses])

  const filterCourses = (courses) => {
    let results = courses
    if (categoryFilter !== "all") {
      results = results.filter((c) => c.category?.name === categoryFilter)
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      results = results.filter((c) =>
        c.title.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q)
      )
    }
    return results
  }

  const filterTracks = (list) =>
    list.map((t) => ({
      ...t,
      courses: filterCourses(t.courses),
    })).filter((t) => t.courses.length > 0)

  const filteredTracks = useMemo(() => filterTracks(tracks), [tracks, categoryFilter, searchQuery])
  const filteredPastTracks = useMemo(
    () => filterTracks(pastTracks),
    [pastTracks, categoryFilter, searchQuery]
  )

  const filteredIndividual = useMemo(
    () => filterCourses(individualCourses),
    [individualCourses, categoryFilter, searchQuery]
  )
  const filteredPast = useMemo(
    () => filterCourses(pastCourses),
    [pastCourses, categoryFilter, searchQuery]
  )

  // Only show tabs that have data, so the UI stays simple when there's just one kind of content
  const tabs = [
    hasTracks && { key: "tracks", label: "Tracks", count: tracks.length },
    hasIndividual && { key: "courses", label: "Individual Courses", count: individualCourses.length },
    hasPast && { key: "past", label: "Past Courses", count: pastTracks.length + pastCourses.length },
  ].filter(Boolean)

  const hasAny = hasTracks || hasIndividual || hasPast

  return (
    <MemberLayout currentUser={currentUser}>
      <Head title="Content - Kingdom Factor" />

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Content</h1>
          <p className="text-zinc-600 mt-1">Tracks and courses assigned to your group.</p>
        </div>

        {hasAny ? (
          <>
            {/* Tabs */}
            {tabs.length > 1 && (
              <div className="flex gap-1 border-b border-zinc-200 overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={cn(
                      "px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
                      activeTab === tab.key
                        ? "border-kf-red text-kf-red"
                        : "border-transparent text-zinc-500 hover:text-zinc-700"
                    )}
                  >
                    {tab.label}
                    <span className="ml-1.5 text-xs text-zinc-400">({tab.count})</span>
                  </button>
                ))}
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <Input
                  placeholder="Search courses..."
                  className="pl-9 bg-white border-zinc-300"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              {categories.length > 1 && (
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-full sm:w-48 bg-white border-zinc-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Tab content */}
            {activeTab === "tracks" && (
              filteredTracks.length > 0 ? (
                <div className="space-y-8">
                  {filteredTracks.map((track) => <TrackSection key={track.id} track={track} />)}
                </div>
              ) : (
                <EmptyState
                  icon={Layers}
                  title="No tracks match your search"
                  description="Try clearing your search or filter."
                />
              )
            )}

            {activeTab === "courses" && (
              filteredIndividual.length > 0 ? (
                <CourseTable courses={filteredIndividual} />
              ) : (
                <EmptyState
                  title="No individual courses match your search"
                  description="Try clearing your search or filter."
                />
              )
            )}

            {activeTab === "past" && (
              filteredPastTracks.length > 0 || filteredPast.length > 0 ? (
                <div className="space-y-8">
                  {filteredPastTracks.map((track) => <TrackSection key={track.id} track={track} />)}
                  <CourseTable courses={filteredPast} isPast />
                </div>
              ) : (
                <EmptyState
                  title="No past courses match your search"
                  description="Try clearing your search or filter."
                />
              )
            )}
          </>
        ) : (
          <EmptyState
            title="No content assigned yet"
            description="Check back later — your coach will assign tracks and courses."
          />
        )}
      </div>
    </MemberLayout>
  )
}
